import { fetchApi } from "@/lib/api/index";
import {
  ExportByIdResponse,
  ExportHistoryResponse,
  ExportLog,
  ExportRequestResponse,
} from "@/types/exports";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

export const useExportHistory = (page: number = 1, limit: number = 50) => {
  return useQuery<ExportHistoryResponse>({
    queryKey: ["export-history", page, limit],
    queryFn: async () => {
      const response = await fetchApi<ExportHistoryResponse>({
        url: "/v2/admin/exports/history",
        method: "GET",
        params: { page, limit },
      });

      if (!response.data) {
        throw new Error("Failed to fetch export history");
      }

      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useExportById = (exportId: string) => {
  return useQuery<ExportLog>({
    queryKey: ["export", exportId],
    queryFn: async () => {
      const response = await fetchApi<ExportByIdResponse>({
        url: `/v2/admin/exports/${exportId}`,
        method: "GET",
      });

      if (!response.data) {
        throw new Error("Failed to fetch export");
      }

      return response.data.export;
    },
    enabled: !!exportId,
  });
};

export const useRequestNCDHHSExport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<ExportRequestResponse, Error>({
    mutationKey: ["request-ncdhhs-export"],
    mutationFn: async () => {
      const response = await fetchApi<ExportRequestResponse>({
        url: "/v2/admin/exports/ncdhhs",
        method: "POST",
      });

      if (!response.data) {
        throw new Error("Failed to request export");
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate export history to refresh the list
      queryClient.invalidateQueries({ queryKey: ["export-history"] });

      toast({
        title: "Export requested",
        description: data.message,
      });
    },
    onError: (error) => {
      toast({
        title: "Export failed",
        description:
          error.message || "Failed to request export. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useRequestMeridianILExport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<ExportRequestResponse, Error>({
    mutationKey: ["request-meridian-il-export"],
    mutationFn: async () => {
      const response = await fetchApi<ExportRequestResponse>({
        url: "/v2/admin/exports/meridian-il",
        method: "POST",
      });

      if (!response.data) {
        throw new Error("Failed to request export");
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["export-history"] });

      toast({
        title: "Export requested",
        description: data.message,
      });
    },
    onError: (error) => {
      toast({
        title: "Export failed",
        description:
          error.message || "Failed to request export. Please try again.",
        variant: "destructive",
      });
    },
  });
};

interface UserContactExportParams {
  queryType: string;
  parameters: Record<string, any>;
}

export const useRequestUserContactExport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<ExportRequestResponse, Error, UserContactExportParams>({
    mutationKey: ["request-user-contact-export"],
    mutationFn: async ({ queryType, parameters }) => {
      const response = await fetchApi<ExportRequestResponse>({
        url: "/v2/admin/exports/users-to-contact",
        method: "POST",
        data: {
          queryType,
          parameters
        }
      });

      if (!response.data) {
        throw new Error("Failed to request export");
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["export-history"] });

      toast({
        title: "Export requested",
        description: data.message,
      });
    },
    onError: (error) => {
      toast({
        title: "Export failed",
        description: error.message || "Failed to request export. Please try again.",
        variant: "destructive",
      });
    },
  });
};